import { useState, type FormEvent } from "react";
import {
  BadgeCheck,
  CalendarClock,
  CreditCard,
  ExternalLink,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

import { ErrorState, Loading } from "@/components/empty-state";
import { FieldError } from "@/components/field-error";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  createSubscriptionCheckout,
  decideOrganisationPlanChange,
  organisationPlanWorkspace,
  requestOrganisationPlanChange,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import type { Organisation } from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";

type Provider = "stripe" | "razorpay";
type Cycle = "monthly" | "annual";

export function PlanEntitlements({
  organisation,
  refresh,
  platformOwner,
  changed,
}: {
  organisation: Organisation;
  refresh: number;
  platformOwner: boolean;
  changed: () => void;
}) {
  const { data, error, loading } = useData(
    () => organisationPlanWorkspace(organisation.id),
    [organisation.id, refresh],
  );
  const [requestPlan, setRequestPlan] = useState<any>();
  const [checkoutPlan, setCheckoutPlan] = useState<any>();
  const [decision, setDecision] = useState<any>();
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  const currentCode = data.subscription?.plan?.code;
  const openRequest = data.requests.find((row) => row.status === "requested");
  const billingReturn = new URLSearchParams(window.location.search).get("billing");
  return (
    <>
      <PageHeader
        eyebrow="SAAS CONTROL"
        title="Plan & entitlements"
        description={`${organisation.name} · Choose provider checkout or preserve a manually approved commercial change.`}
      />
      {billingReturn && (
        <section className="boundary-panel mb-5" aria-live="polite">
          <CreditCard className="size-5" aria-hidden="true" />
          <div>
            <h2>{billingReturn === "success" ? "Checkout returned successfully" : "Checkout was cancelled"}</h2>
            <p>
              {billingReturn === "success"
                ? "RAQOZ will activate the plan only after the provider's signed webhook confirms the paid subscription period. Refresh to see the latest state."
                : "No plan was activated. You can start a new checkout whenever you are ready."}
            </p>
          </div>
          <Button variant="outline" onClick={changed}>
            <RefreshCw className="size-4" />Refresh status
          </Button>
        </section>
      )}
      <section className="boundary-panel mb-5" aria-labelledby="billing-boundary">
        <ShieldCheck className="size-5" aria-hidden="true" />
        <div>
          <h2 id="billing-boundary">Commercial boundary</h2>
          <p>{data.billing_boundary}</p>
        </div>
      </section>
      <section className="grid gap-4 lg:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <p className="eyebrow">CURRENT WORKSPACE</p>
          <div className="mt-3 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-3xl font-semibold">{data.subscription?.plan?.name || "No plan"}</h2>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">{data.subscription?.plan?.description}</p>
            </div>
            <Badge value={data.subscription?.status || "unavailable"} />
          </div>
          <dl className="mt-5 grid gap-3 text-sm">
            <Fact label="Activation source" value={titleCase(data.subscription?.activation_source || "self_service_free")} />
            <Fact label="Billing provider" value={data.subscription?.billing_provider ? titleCase(data.subscription.billing_provider) : "Manual / free"} />
            <Fact label="Current period" value={data.subscription?.current_period_ends_at ? `Until ${displayDate(data.subscription.current_period_ends_at)}` : "No paid period recorded"} />
          </dl>
          <h3 className="mt-6 text-sm font-semibold">Entitlement snapshot</h3>
          <ul className="mt-3 grid gap-2 text-sm">
            {(data.subscription?.entitlement_snapshot || []).map((item: string) => (
              <li className="flex gap-2" key={item}>
                <BadgeCheck className="mt-0.5 size-4 shrink-0 text-[var(--teal-deep)]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </Panel>
        <Panel>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="eyebrow">PLAN OPTIONS</p>
              <h2 className="mt-1 text-2xl font-semibold">Provider checkout or manual review</h2>
            </div>
            {openRequest && <Badge value="requested" />}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.plans.map((plan) => {
              const primaryPrice = plan.prices?.find((price: any) => price.cycle === "monthly") || plan.prices?.[0];
              const checkoutEligible = plan.code !== "launch" && !plan.annual_contract_required && plan.prices?.some((price: any) => price.cycle !== "free");
              return (
                <article className="rounded-xl border border-[var(--border)] p-4" key={plan.code}>
                  <div className="flex items-start justify-between gap-2"><strong>{plan.name}</strong><Badge value={plan.availability} /></div>
                  <p className="mt-2 text-xs leading-5 text-[var(--text-secondary)]">{plan.description}</p>
                  {primaryPrice && primaryPrice.cycle !== "free" && (
                    <p className="mt-3 text-sm font-semibold tabular-nums">
                      {money(Number(primaryPrice.amount), primaryPrice.currency)} / {primaryPrice.cycle === "monthly" ? "month" : "year"}
                    </p>
                  )}
                  {plan.code === currentCode ? (
                    <p className="mt-4 text-xs font-semibold">Current plan</p>
                  ) : (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {checkoutEligible && (
                        <Button size="sm" disabled={Boolean(openRequest)} onClick={() => setCheckoutPlan(plan)}>
                          <CreditCard className="size-4" />Checkout
                        </Button>
                      )}
                      <Button size="sm" variant="outline" disabled={Boolean(openRequest)} onClick={() => setRequestPlan(plan)}>
                        Manual request
                      </Button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          {openRequest && <p className="mt-4 text-xs text-[var(--text-secondary)]">Resolve the open manual plan request before starting a provider checkout.</p>}
        </Panel>
      </section>
      <Panel className="mt-5">
        <h2 className="text-2xl font-semibold">Checkout history</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="data-table">
            <thead><tr><th scope="col">Created</th><th scope="col">Provider</th><th scope="col">Plan</th><th scope="col">Cycle</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {data.checkout_sessions.map((row) => (
                <tr key={row.id}><td className="tabular-nums">{displayDate(row.created_at, true)}</td><td>{titleCase(row.provider)}</td><td>{row.plan_name}</td><td>{titleCase(row.cycle)}</td><td><Badge value={row.status} /></td></tr>
              ))}
            </tbody>
          </table>
          {!data.checkout_sessions.length && <p className="p-6 text-center text-sm text-[var(--text-secondary)]">No provider checkout has been started.</p>}
        </div>
      </Panel>
      <Panel className="mt-5">
        <h2 className="text-2xl font-semibold">Manual plan request history</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="data-table">
            <thead><tr><th scope="col">Requested</th><th scope="col">Change</th><th scope="col">Reason</th><th scope="col">Status</th><th scope="col">Decision</th><th scope="col">Action</th></tr></thead>
            <tbody>{data.requests.map((row) => <tr key={row.id}><td className="tabular-nums">{displayDate(row.requested_at,true)}<small className="block text-[var(--text-secondary)]">{row.requester?.name}</small></td><td><strong>{row.current_plan?.name} → {row.requested_plan?.name}</strong></td><td className="max-w-sm text-xs text-[var(--text-secondary)]">{row.request_reason}</td><td><Badge value={row.status}/></td><td className="max-w-sm text-xs text-[var(--text-secondary)]">{row.decision_reason || "Awaiting independent decision"}</td><td>{platformOwner && row.status === "requested" && <Button size="sm" onClick={() => setDecision(row)}>Review</Button>}</td></tr>)}</tbody>
          </table>
          {!data.requests.length && <p className="p-6 text-center text-sm text-[var(--text-secondary)]">No manual plan change has been requested.</p>}
        </div>
      </Panel>
      <BillingCheckoutDialog
        plan={checkoutPlan}
        priceBook={data.price_book}
        open={Boolean(checkoutPlan)}
        onOpenChange={(open) => !open && setCheckoutPlan(undefined)}
        onSubmit={async (input) => {
          const checkout = await createSubscriptionCheckout(organisation.id, { planCode: checkoutPlan.code, ...input });
          window.location.assign(checkout.checkoutUrl);
        }}
      />
      <PlanRequestDialog plan={requestPlan} open={Boolean(requestPlan)} onOpenChange={(open) => !open && setRequestPlan(undefined)} onSubmit={async (reason) => { await requestOrganisationPlanChange(organisation.id,requestPlan.code,reason); setRequestPlan(undefined); changed(); }} />
      <PlanDecisionDialog request={decision} open={Boolean(decision)} onOpenChange={(open) => !open && setDecision(undefined)} onSubmit={async (input) => { await decideOrganisationPlanChange(organisation.id,decision.id,input); setDecision(undefined); changed(); }} />
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between gap-3"><dt className="text-[var(--text-secondary)]">{label}</dt><dd className="font-semibold tabular-nums">{value}</dd></div>;
}

function BillingCheckoutDialog({plan,priceBook,open,onOpenChange,onSubmit}:{plan:any;priceBook:string;open:boolean;onOpenChange:(open:boolean)=>void;onSubmit:(input:{provider:Provider;cycle:Cycle})=>Promise<void>}) {
  const [provider,setProvider]=useState<Provider>(priceBook === "INDIA_INR" ? "razorpay" : "stripe");
  const [cycle,setCycle]=useState<Cycle>("monthly");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const cycles = (plan?.prices || []).filter((price:any)=>price.cycle==="monthly"||price.cycle==="annual");
  const selectedPrice = cycles.find((price:any)=>price.cycle===cycle) || cycles[0];
  async function submit(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");try{await onSubmit({provider,cycle:selectedPrice?.cycle || cycle});}catch(reason){setError((reason as Error).message);}finally{setBusy(false);}}
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader eyebrow="SECURE SUBSCRIPTION" title={`Subscribe to ${plan?.name || "plan"}`} description="Checkout occurs on the selected provider. RAQOZ activates entitlements only after verifying the provider's signed webhook."/><form className="grid gap-4 p-6" onSubmit={submit}><Label>Billing provider<Select value={provider} onChange={(event)=>setProvider(event.target.value as Provider)}><option value="razorpay">Razorpay</option><option value="stripe">Stripe</option></Select></Label><Label>Billing cycle<Select value={selectedPrice?.cycle || cycle} onChange={(event)=>setCycle(event.target.value as Cycle)}>{cycles.map((price:any)=><option key={price.cycle} value={price.cycle}>{titleCase(price.cycle)} · {money(Number(price.amount),price.currency)}</option>)}</Select></Label><p className="text-xs text-[var(--text-secondary)]">Provider credentials and immutable price IDs stay in the server runtime. No secret or price override is sent from the browser.</p><FieldError>{error}</FieldError><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button disabled={busy||!selectedPrice} type="submit">{busy?"Opening checkout…":<><ExternalLink className="size-4"/>Continue to provider</>}</Button></div></form></DialogContent></Dialog>;
}

function PlanRequestDialog({plan,open,onOpenChange,onSubmit}:{plan:any;open:boolean;onOpenChange:(open:boolean)=>void;onSubmit:(reason:string)=>Promise<void>}) {
  const [busy,setBusy]=useState(false); const [error,setError]=useState("");
  async function submit(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget);setBusy(true);setError("");try{await onSubmit(String(form.get("reason")));}catch(reason){setError((reason as Error).message);}finally{setBusy(false);}}
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader eyebrow="PLAN REQUEST" title={`Request ${plan?.name || "plan"}`} description="Use this route for negotiated contracts or when provider checkout is unsuitable. This manual route does not charge a card or activate a paid entitlement."/><form className="grid gap-4 p-6" onSubmit={submit}><Label>Business reason<Textarea name="reason" required minLength={20} placeholder="Explain the operating need and expected plan scope."/></Label><FieldError>{error}</FieldError><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button disabled={busy} type="submit">{busy?"Recording…":"Submit for approval"}</Button></div></form></DialogContent></Dialog>;
}

function PlanDecisionDialog({request,open,onOpenChange,onSubmit}:{request:any;open:boolean;onOpenChange:(open:boolean)=>void;onSubmit:(input:{decision:"approved"|"rejected";reason:string;externalReference?:string;currentPeriodEndsAt?:string})=>Promise<void>}) {
  const [decision,setDecision]=useState<"approved"|"rejected">("approved"); const [busy,setBusy]=useState(false); const [error,setError]=useState(""); const paid=request?.requested_plan?.code!=="launch";
  async function submit(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget);setBusy(true);setError("");try{await onSubmit({decision,reason:String(form.get("reason")),externalReference:decision==="approved"?String(form.get("externalReference")||""):undefined,currentPeriodEndsAt:decision==="approved"&&form.get("periodEnd")?new Date(String(form.get("periodEnd"))).toISOString():undefined});}catch(reason){setError((reason as Error).message);}finally{setBusy(false);}}
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader eyebrow="INDEPENDENT APPROVAL" title={`Review ${request?.requested_plan?.name || "plan"} request`} description="Approval activates only the entitlement snapshot recorded here. It does not collect or independently verify payment."/><form className="grid gap-4 p-6" onSubmit={submit}><div className="grid grid-cols-2 gap-2"><Button type="button" variant={decision==="approved"?"default":"outline"} onClick={()=>setDecision("approved")}><ShieldCheck className="size-4"/>Approve</Button><Button type="button" variant={decision==="rejected"?"destructive":"outline"} onClick={()=>setDecision("rejected")}>Reject</Button></div>{decision==="approved"&&paid&&<><Label>External contract or billing reference<Input name="externalReference" required/></Label><Label>Paid period ends<Input name="periodEnd" type="datetime-local" required/><span className="mt-1 flex items-center gap-1 text-xs text-[var(--text-secondary)]"><CalendarClock className="size-3"/>Must be a future date.</span></Label></>}<Label>Decision reason<Textarea name="reason" required minLength={10}/></Label><FieldError>{error}</FieldError><div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button disabled={busy} type="submit">{busy?"Recording…":"Record decision"}</Button></div></form></DialogContent></Dialog>;
}
